import classNames from 'classnames';

import React, {
  Children,
  FC,
  forwardRef,
  ForwardRefExoticComponent,
  HTMLAttributes,
  ReactElement,
  ReactNode,
  RefAttributes,
} from 'react';

import { Counter } from '../counter';
import { Icon as IconComponent } from '../icon';
import { Typography } from '../typography';

import styles from './content.scss';

type Props = RefAttributes<HTMLDivElement> &
  HTMLAttributes<HTMLDivElement> & {
    readonly text?: string | ReactElement;
    readonly subtitle?: string | ReactElement;
    readonly variant?: string;
    readonly counter?: number;
    readonly icon?: string | ReactElement;
    readonly image?: ReactNode;
    readonly addon?: ReactElement;
    readonly inverted?: boolean;
    readonly multiline?: boolean;
    readonly dataTestId?: string;
  };

const COMPONENT_ID = 'modulor-content';

const Text: FC<Props> = ({ text, subtitle, inverted, multiline, variant, dataTestId }: Props) => {
  let contentTitle;
  let contentSubtitle;

  if (text) {
    contentTitle =
      typeof text === 'string' ? (
        <Typography variant={variant} dataTestId={`${dataTestId}-title`}>
          {text}
        </Typography>
      ) : (
        text
      );
  }

  if (subtitle) {
    contentSubtitle =
      typeof subtitle === 'string' ? (
        <Typography
          className={styles.subtitle}
          variant="caption-1-regular"
          dataTestId={`${dataTestId}-subtitle`}>
          {subtitle}
        </Typography>
      ) : (
        subtitle
      );
  }

  return (
    <div
      className={classNames(styles.text, {
        [styles.inverted]: inverted,
        [styles.multiline]: multiline,
        [styles['without-padding']]: Boolean(subtitle),
      })}
      data-testid={dataTestId}>
      {contentTitle}
      {contentSubtitle}
    </div>
  );
};

const Icon: FC<Props> = ({ icon, dataTestId }: Props) =>
  typeof icon === 'string' ? <IconComponent name={icon} dataTestId={dataTestId} /> : icon;

const Addon: FC<Props> = ({ addon, counter, dataTestId }: Props) => (
  <div className={styles.addon}>
    {addon ? (
      addon
    ) : counter !== null ? (
      <Counter value={counter} dataTestId={`${dataTestId}-counter`} />
    ) : null}
  </div>
);

export const Content: ForwardRefExoticComponent<Props> = forwardRef<HTMLDivElement, Props>(
  (
    {
      text,
      subtitle,
      variant = 'body-regular',
      addon,
      counter = null,
      icon,
      image,
      inverted,
      multiline,
      className,
      dataTestId = COMPONENT_ID,
      children,
      ...rest
    },
    ref,
  ) => {
    const classes = classNames(className, styles.container);

    return (
      <div
        className={classes}
        ref={ref}
        data-id={dataTestId}
        data-testid={dataTestId}
        data-component-name={COMPONENT_ID}
        data-variant={variant}
        {...rest}>
        {Children.toArray(children).length > 0 ? (
          <div className={styles.control} data-testid={`${dataTestId}-controls-container`}>
            {children}
          </div>
        ) : null}
        {image ? image : null}
        {icon ? <Icon icon={icon} dataTestId={`${dataTestId}-icon`} /> : null}
        {text || subtitle ? (
          <Text
            text={text}
            subtitle={subtitle}
            inverted={inverted}
            multiline={multiline}
            variant={variant}
            dataTestId={`${dataTestId}-text`}
          />
        ) : null}
        {addon || counter ? (
          <Addon addon={addon} counter={counter} dataTestId={`${dataTestId}-addon`} />
        ) : null}
      </div>
    );
  },
);

Content.displayName = 'Content';
